/*
This file is part of the Notesnook project (https://notesnook.com/)

Copyright (C) 2023 Streetwriters (Private) Limited

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU General Public License for more details.

You should have received a copy of the GNU General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import "./polyfills";
import "./app.css";
import { unregister } from "./service-worker-registration";
import { register as registerStreamSaver } from "./utils/stream-saver/mitm";
import { ThemeDark, ThemeLight, themeToCSS } from "@notesnook/theme";
import Config from "./utils/config";
import { setI18nGlobal, Messages } from "@notesnook/intl";
import { i18n } from "@lingui/core";
import { installZhTooltipLocalization } from "./utils/zh-tooltip-localizer";

const colorScheme = JSON.parse(
  window.localStorage.getItem("colorScheme") || '"light"'
);
const root = document.querySelector("html");
if (root) root.setAttribute("data-theme", colorScheme);

const theme =
  colorScheme === "dark"
    ? Config.get("theme:dark", ThemeDark)
    : Config.get("theme:light", ThemeLight);
const stylesheet = document.getElementById("theme-colors");
if (theme) {
  const css = themeToCSS(theme);
  if (stylesheet) stylesheet.innerHTML = css;
} else stylesheet?.remove();

const savedLocale = window.localStorage.getItem("interface-language");
const activeLocale =
  savedLocale === "en" || savedLocale === "zh-CN"
    ? savedLocale
    : navigator.language.toLowerCase().startsWith("zh")
      ? "zh-CN"
      : "en";

const locales = Promise.all([
  import.meta.env.DEV
    ? import("@notesnook/intl/locales/$pseudo-LOCALE.json")
    : import("@notesnook/intl/locales/$en.json"),
  import("@notesnook/intl/locales/$zh-CN.json")
]);
locales.then(([english, chinese]) => {
  i18n.load({
    en: english.default.messages as unknown as Messages,
    "zh-CN": chinese.default.messages as unknown as Messages
  });
  i18n.activate(activeLocale);
  document.documentElement.lang = activeLocale;
  installZhTooltipLocalization();

  performance.mark("import:root");
  import("./root").then(({ startApp }) => {
    performance.mark("start:app");
    startApp();
  });
});
setI18nGlobal(i18n);

if (!IS_DESKTOP_APP) {
  //   logger.info("Initializing service worker...");

  // This self-hosted web app prioritizes immediate updates over offline
  // caching, preventing an older cached client from running stale code.
  unregister();
  registerStreamSaver();

  // window.addEventListener("beforeinstallprompt", () => showInstallNotice());
}
